import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Paper, Stack, Typography} from "@mui/material";
import {ArrowBack, Edit, LinkOutlined, PublicOutlined, TitleOutlined} from "@mui/icons-material";
import {useHistory, useParams} from "react-router-dom";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import IconButton from "../../../../../../components/base/button/icon_button";
import Button from "../../../../../../components/base/button/button";
import Form from "../../../../../../components/base/form";
import {OutlinedInputForm} from "../../../../../../components/base/input/outlined_input";
import CKEditorTextfield from "../../../../../../components/text-fields/ck-editor-textfield";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import Api from "../../../../../../../core/services/api/api";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import {Checkbox} from "../../../../../../components/base/checkbox/checkbox";
import {ReactComponent as Passed} from "../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../assets/images/validations/failed.svg";

const titleSchema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(ValidateMessages.required),
});

const routeSchema = Yup.object().shape({
    route: Yup.string().trim().nullable().required(ValidateMessages.required),
});

const validateTitle = makeValidate(titleSchema);
const requiredTitle = makeRequired(titleSchema);
const validateRoute = makeValidate(routeSchema);
const requiredRoute = makeRequired(routeSchema);

const DynamicPageSingleView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'user-platform.dynamic-pages.single'});
    const history = useHistory();
    const params = useParams();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('dynamic-page-single-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canManage = operatorAccessLevels.packages.clientPlatform.manageDynamicPages;

    const pageId = params?.pageId;

    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [titleDraft, setTitleDraft] = useState('');
    const [routeDraft, setRouteDraft] = useState('');
    const [contentDraft, setContentDraft] = useState('');
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingRoute, setEditingRoute] = useState(false);
    const [updatingTitle, setUpdatingTitle] = useState(false);
    const [updatingRoute, setUpdatingRoute] = useState(false);
    const [updatingIsPublic, setUpdatingIsPublic] = useState(false);
    const [updatingContent, setUpdatingContent] = useState(false);
    const [removing, setRemoving] = useState(false);

    /**
     * As soon as the component is mounted and whenever the page id param changes:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformDynamicPageSingle(pageId));
        configureBehavioralTool((tags) => tags.userPlatformDynamicPageSingle(pageId));
    }, [configureBehavioralTool, configureNotebookTags, pageId]);

    /**
     * Applies server page data to local state and synchronizes all editable drafts.
     *
     * @param {import("../../../../../../../core/services/api/type-declerations/response").DynamicPageDs} data
     */
    const applyPageData = useCallback((data) => {
        setPage(data);
        setTitleDraft(data?.title ?? '');
        setRouteDraft(data?.route ?? '');
        setContentDraft(data?.content ?? '');
    }, []);

    /**
     * Loads the dynamic page from the server for the current route param.
     *
     * @return {Promise<void>}
     */
    const loadPage = useCallback(async () => {
        if (!pageId)
            return;
        setLoading(true);
        const response = await Api.getDynamicPage(pageId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            applyPageData(mapDynamicPageFromApi(response.data));
        }
    }, [applyPageData, isMounted, pageId]);

    /**
     * With each change in the page id, loads the dynamic page data from the server.
     */
    useEffect(() => {
        void loadPage();
    }, [loadPage]);

    const hasContentChanges = useMemo(() =>
            (contentDraft ?? '') !== (page?.content ?? ''),
        [contentDraft, page?.content]);

    /**
     * Builds a full update payload from the committed page state and optional field overrides.
     *
     * @param {Partial<import("../../../../../../../core/services/api/type-declerations/response").DynamicPageDs>} overrides
     * @return {import("../../../../../../../core/services/api/type-declerations/response").DynamicPageDs|null}
     */
    const buildUpdatePayload = useCallback((overrides = {}) => {
        if (!page)
            return null;
        return {
            id: page.id,
            title: overrides.title ?? page.title,
            route: overrides.route ?? page.route,
            content: overrides.content ?? page.content,
            isPublic: overrides.isPublic ?? page.isPublic,
        };
    }, [page]);

    /**
     * Persists an updated page title when the user confirms inline edits.
     *
     * @param {{ title: string }} values
     * @return {Promise<void>}
     */
    const saveTitle = useCallback(async (values) => {
        const trimmedTitle = values.title?.trim() ?? '';
        if (trimmedTitle === (page?.title ?? '')) {
            setEditingTitle(false);
            return;
        }
        setUpdatingTitle(true);
        const response = await Api.updateDynamicPage(mapDynamicPageToApi(buildUpdatePayload({title: trimmedTitle})));
        if (!isMounted())
            return;
        setUpdatingTitle(false);
        if (response?.resultFlag) {
            applyPageData(mapDynamicPageFromApi(response.data));
            setEditingTitle(false);
        }
    }, [applyPageData, buildUpdatePayload, isMounted, page?.title]);

    /**
     * Persists an updated page route when the user confirms inline edits.
     *
     * @param {{ route: string }} values
     * @return {Promise<void>}
     */
    const saveRoute = useCallback(async (values) => {
        const trimmedRoute = values.route?.trim() ?? '';
        if (trimmedRoute === (page?.route ?? '')) {
            setEditingRoute(false);
            return;
        }
        setUpdatingRoute(true);
        const response = await Api.updateDynamicPage(mapDynamicPageToApi(buildUpdatePayload({route: trimmedRoute})));
        if (!isMounted())
            return;
        setUpdatingRoute(false);
        if (response?.resultFlag) {
            applyPageData(mapDynamicPageFromApi(response.data));
            setEditingRoute(false);
        }
    }, [applyPageData, buildUpdatePayload, isMounted, page?.route]);

    /**
     * Persists an updated isPublic flag when the user toggles the checkbox.
     *
     * @param {boolean} isPublic
     * @return {Promise<void>}
     */
    const saveIsPublic = useCallback(async (isPublic) => {
        if (isPublic === (page?.isPublic ?? false))
            return;
        setUpdatingIsPublic(true);
        const response = await Api.updateDynamicPage(mapDynamicPageToApi(buildUpdatePayload({isPublic})));
        if (!isMounted())
            return;
        setUpdatingIsPublic(false);
        if (response?.resultFlag) {
            applyPageData(mapDynamicPageFromApi(response.data));
        }
    }, [applyPageData, buildUpdatePayload, isMounted, page?.isPublic]);

    /**
     * Persists the current CKEditor content draft when the user confirms changes.
     *
     * @return {Promise<void>}
     */
    const saveContent = useCallback(async () => {
        if (!hasContentChanges)
            return;
        setUpdatingContent(true);
        const response = await Api.updateDynamicPage(mapDynamicPageToApi(buildUpdatePayload({content: contentDraft ?? ''})));
        if (!isMounted())
            return;
        setUpdatingContent(false);
        if (response?.resultFlag) {
            applyPageData(mapDynamicPageFromApi(response.data));
        }
    }, [applyPageData, buildUpdatePayload, contentDraft, hasContentChanges, isMounted]);

    /**
     * Discards unsaved CKEditor changes and restores the last committed content.
     */
    const rollbackContent = useCallback(() => {
        setContentDraft(page?.content ?? '');
    }, [page?.content]);

    /**
     * Removes the current dynamic page from the server and navigates back to the list.
     *
     * @return {Promise<void>}
     */
    const removePage = useCallback(async () => {
        setRemoving(true);
        const response = await Api.removeDynamicPage(page.id);
        if (!isMounted())
            return;
        setRemoving(false);
        if (response?.resultFlag) {
            history.replace(routes.userPlatform.dynamicPages.list);
        }
    }, [history, isMounted, page?.id]);

    /**
     * Opens a confirmation dialog before deleting the current dynamic page.
     */
    const showDeleteConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-2'}>
                <p className={'text-sm text-color-1 mb-0'}>
                    {t('deleteConfirmation')}
                </p>
            </div>,
            {},
            (decision) => decision && removePage(),
        );
    }, [removePage, t]);

    /**
     * Navigates the user back to the dynamic pages list.
     */
    const goBack = useCallback(() => {
        history.push(routes.userPlatform.dynamicPages.list);
    }, [history]);

    return (
        <Box className={'dynamic-page-single-view w-100'} sx={{py: 2}}>
            {
                loading &&
                <Box sx={{minHeight: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <LoadingLottieAnimation width={'80%'}/>
                </Box>
            }
            {
                !loading && !page &&
                <Typography className={'text-sm text-color-1'} align={'center'}>
                    {t('notFound')}
                </Typography>
            }
            {
                !loading && !!page &&
                <>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        sx={{mb: 2, gap: 2, flexWrap: 'wrap'}}
                    >
                        <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{minWidth: 0}}>
                            <IconButton color={'secondary'} onPressed={goBack} aria-label={t('backTooltip')}>
                                <ArrowBack className='icon normal'/>
                            </IconButton>
                            <Typography variant={'h5'} component={'h1'} className={'text-xl font-weight-semi-bold'}>
                                {t('pageHeaderTitle')}
                            </Typography>
                        </Stack>
                        {
                            canManage &&
                            <Button
                                variant={'outlined'}
                                color={'error'}
                                onClick={showDeleteConfirmation}
                                disabled={removing}
                            >
                                {t('deleteButton')}
                            </Button>
                        }
                    </Stack>

                    <Paper className={'paper w-100'} sx={{p: 3}}>
                        <Stack spacing={2.5} sx={{width: '100%'}}>
                            <Stack spacing={1.5} sx={{width: '100%'}}>
                                <DynamicPageInfoCard
                                    icon={TitleOutlined}
                                    label={t('titleLabel')}
                                    value={titleDraft || page.title}
                                    placeholder={t('titlePlaceholder')}
                                    canManage={canManage}
                                    editing={editingTitle}
                                    saving={updatingTitle}
                                    onEdit={() => setEditingTitle(true)}
                                    onCancelEdit={() => {
                                        setTitleDraft(page.title);
                                        setEditingTitle(false);
                                    }}
                                    fieldName={'title'}
                                    committedValue={page.title}
                                    validate={validateTitle}
                                    requiredField={requiredTitle.title}
                                    onSubmit={saveTitle}
                                    onDraftChange={setTitleDraft}
                                />
                                <DynamicPageInfoCard
                                    icon={LinkOutlined}
                                    label={t('routeLabel')}
                                    value={routeDraft || page.route}
                                    placeholder={t('routePlaceholder')}
                                    canManage={canManage}
                                    editing={editingRoute}
                                    saving={updatingRoute}
                                    onEdit={() => setEditingRoute(true)}
                                    onCancelEdit={() => {
                                        setRouteDraft(page.route);
                                        setEditingRoute(false);
                                    }}
                                    fieldName={'route'}
                                    committedValue={page.route}
                                    validate={validateRoute}
                                    requiredField={requiredRoute.route}
                                    onSubmit={saveRoute}
                                    onDraftChange={setRouteDraft}
                                />
                                <DynamicPageIsPublicCard
                                    label={t('isPublicLabel')}
                                    isPublic={page.isPublic}
                                    isPublicYesLabel={t('isPublicYes')}
                                    isPublicNoLabel={t('isPublicNo')}
                                    canManage={canManage}
                                    saving={updatingIsPublic}
                                    onToggle={saveIsPublic}
                                />
                            </Stack>
                            <Box>
                                <Stack direction='row' sx={{marginBlockEnd: 1, minHeight: 40}} justifyContent={'space-between'} alignItems={'center'}>
                                    <Typography className={'text-md font-weight-semi-bold'} component={'h2'}>
                                        {t('contentSectionTitle')}
                                    </Typography>
                                    {
                                        canManage &&
                                        <SaveOrRollback
                                            show={hasContentChanges}
                                            loading={updatingContent}
                                            onSave={saveContent}
                                            onRollback={rollbackContent}
                                            column={false}
                                        />
                                    }
                                </Stack>
                                {
                                    canManage
                                        ? <CKEditorTextfield
                                            form={false}
                                            value={contentDraft}
                                            onChange={setContentDraft}
                                        />
                                        : <Box
                                            className={'dynamic-page-content-view ck-content'}
                                            sx={{
                                                p: 2,
                                                border: 1,
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                bgcolor: 'background.paper',
                                            }}
                                            dangerouslySetInnerHTML={{__html: page.content ?? ''}}
                                        />
                                }
                            </Box>
                        </Stack>
                    </Paper>
                </>
            }
        </Box>
    );
};

/**
 * Editable info card for title and route fields on the single page view.
 */
const DynamicPageInfoCard = ({
                                 icon: Icon,
                                 label,
                                 value,
                                 placeholder,
                                 canManage,
                                 editing,
                                 saving,
                                 onEdit,
                                 onCancelEdit,
                                 fieldName,
                                 committedValue,
                                 validate,
                                 requiredField,
                                 onSubmit,
                                 onDraftChange,
                             }) => (
    <Paper
        elevation={0}
        variant={'outlined'}
        sx={{
            position: 'relative',
            width: '100%',
            px: 2,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
            borderColor: 'divider',
        }}
    >
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                opacity: 0.92,
                mt: 0.25,
            }}
        >
            <Icon sx={{fontSize: 18}}/>
        </Box>
        <Box sx={{flex: 1, minWidth: 0, pr: canManage && !editing ? 3 : 0}}>
            {
                !editing &&
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    display={'block'}
                    lineHeight={1.3}
                    sx={{letterSpacing: 0.2, textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, mb: 0.5}}
                >
                    {label}
                </Typography>
            }
            {
                editing
                    ? <Form
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={{[fieldName]: committedValue}}
                        render={({values, form}) => (
                            <Stack spacing={1} direction={'row'}>
                                <OutlinedInputForm
                                    fullWidth
                                    name={fieldName}
                                    label={label}
                                    placeholder={placeholder}
                                    required={requiredField}
                                    value={values[fieldName] ?? ''}
                                    onChange={(e) => {
                                        form.change(fieldName, e.target.value);
                                        onDraftChange(e.target.value);
                                    }}
                                />
                                <SaveOrRollback
                                    show
                                    loading={saving}
                                    onSave={form.submit}
                                    onRollback={() => {
                                        form.change(fieldName, committedValue);
                                        onCancelEdit();
                                    }}
                                    column={false}
                                />
                            </Stack>
                        )}
                    />
                    : <Typography
                        variant={'body2'}
                        fontWeight={600}
                        color={'text.primary'}
                        sx={{wordBreak: 'break-word'}}
                    >
                        {value}
                    </Typography>
            }
        </Box>
        {
            canManage && !editing &&
            <Box sx={{position: 'absolute', top: 8, right: 8}}>
                <IconButton color={'secondary'} onPressed={onEdit}>
                    <Edit className='icon small'/>
                </IconButton>
            </Box>
        }
    </Paper>
);

/**
 * Displays the isPublic status as an indicator in view mode, or as a checkbox in manage mode.
 */
const DynamicPageIsPublicCard = ({
                                     label,
                                     isPublic,
                                     isPublicYesLabel,
                                     isPublicNoLabel,
                                     canManage,
                                     saving,
                                     onToggle,
                                 }) => (
    <Paper
        elevation={0}
        variant={'outlined'}
        sx={{
            width: '100%',
            px: 2,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
            borderColor: 'divider',
        }}
    >
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                opacity: 0.92,
            }}
        >
            <PublicOutlined sx={{fontSize: 18}}/>
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
            {
                !canManage &&
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    display={'block'}
                    lineHeight={1.3}
                    sx={{letterSpacing: 0.2, textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, mb: 0.5}}
                >
                    {label}
                </Typography>
            }
            {
                canManage
                    ? <Checkbox
                        label={label}
                        checked={isPublic ?? false}
                        disabled={saving}
                        onChange={(e) => onToggle(e.target.checked)}
                        formControlLabelProps={{
                            sx: {margin: 0},
                        }}
                    />
                    : <Stack direction={'row'} alignItems={'center'} spacing={1}>
                        {
                            isPublic
                                ? <Passed className={'icon normal'}/>
                                : <Failed className={'icon normal'}/>
                        }
                        <Typography variant={'body2'} fontWeight={600} color={'text.primary'}>
                            {isPublic ? isPublicYesLabel : isPublicNoLabel}
                        </Typography>
                    </Stack>
            }
        </Box>
    </Paper>
);

export default DynamicPageSingleView;

/**
 * Maps a CustomisePage API entity to the UI dynamic page model.
 *
 * @param {import("../../../../../../../core/services/api/type-declerations/response").CustomisePageApiDs} data
 * @return {import("../../../../../../../core/services/api/type-declerations/response").DynamicPageDs}
 */
const mapDynamicPageFromApi = (data) => ({
    id: data.id,
    title: data.title ?? '',
    route: data.name ?? '',
    content: data.pageContent ?? '',
    isPublic: data.isPublic ?? false,
});

/**
 * Maps the UI dynamic page model to a CustomisePage API request body.
 *
 * @param {{ id?: number, title: string, route: string, content?: string, isPublic?: boolean }} page
 * @return {import("../../../../../../../core/services/api/type-declerations/response").CustomisePageApiRequestDs}
 */
const mapDynamicPageToApi = ({id, title, route, content, isPublic}) => ({
    ...(id != null ? {id} : {}),
    name: route,
    title,
    pageContent: content ?? '',
    isPublic: isPublic ?? false,
    isPopup: false,
});
